import {
  getChartAssetTag,
  stripChartParams,
} from '@terminal/modules/simple-chart/utils/chart-params';
import { TabsType } from '@terminal/modules/tab-content/types';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useMatch, useNavigate, useSearchParams } from 'react-router';

interface NavigateToPageProps {
  entityTag: string;
  source: PosthogTrackingSource;
  searchParams?: URLSearchParams;
  tab?: TabsType;
  financialsStatement?: string;
  replace?: boolean;
}

export const useNavigateToAssetOverview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnAssetOverview = !!useMatch(appPaths.assetOverview);
  const { trackAssetOpen } = usePosthogEvents();

  // The asset tag the user is currently viewing on AO, or null otherwise.
  // Used to suppress asset_open when navigating within the same asset's AO
  // (e.g. tab/subtab change), per the "navigates to, not within" principle.
  const currentAssetTag = isOnAssetOverview
    ? searchParams.get(queryKeys.assetOverviewAsset)
    : null;

  const navigateToAssetOverview = (
    entityTag: string,
    source: PosthogTrackingSource,
    options?: { replace?: boolean }
  ) => {
    if (currentAssetTag !== entityTag) {
      trackAssetOpen(source, [entityTag]);
    }

    const newSearchParams = new URLSearchParams();

    newSearchParams.set(queryKeys.assetOverviewAsset, entityTag);
    newSearchParams.set(queryKeys.assetOverviewTab, TabsType.Overview);

    const to = {
      pathname: appPaths.assetOverview,
      search: newSearchParams.toString(),
    };

    navigate(to, { replace: options?.replace });
  };

  const navigateToPage = ({
    entityTag,
    source,
    searchParams: nextSearchParams,
    tab = TabsType.Overview,
    financialsStatement,
    replace,
  }: NavigateToPageProps) => {
    if (currentAssetTag !== entityTag) {
      trackAssetOpen(source, [entityTag]);
    }

    let newSearchParams = new URLSearchParams(nextSearchParams);
    const previousAssetTag = getChartAssetTag(newSearchParams);

    if (previousAssetTag && previousAssetTag !== entityTag) {
      newSearchParams = stripChartParams(newSearchParams, false);
    }

    newSearchParams.set(queryKeys.assetOverviewAsset, entityTag);
    newSearchParams.set(queryKeys.assetOverviewTab, tab);

    if (financialsStatement) {
      newSearchParams.set(
        queryKeys.activeFinancialsStatement,
        financialsStatement
      );
    }

    newSearchParams.delete(queryKeys.widget);

    const to = {
      pathname: appPaths.assetOverview,
      search: newSearchParams.toString(),
    };

    navigate(to, { replace });
  };

  return {
    navigateToAssetOverview,
    navigateToPage,
  };
};
